import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import type {
  DashboardBilledDaysByJobType,
  DashboardClientOption,
  DashboardDatePreset,
  DashboardKpiMetric,
  DashboardRecentTimeEntry,
  DashboardSummaryResponse,
  DashboardTimeSeriesPoint,
  DashboardTopActiveProspectByBilledDays,
  DashboardTopClientChip,
} from "../types/apiSchemaAliases";
import type { ApiClient } from "../types/UserTypes";
import { createApiClient } from "../utils/apiClient";
import { useTier } from "../utils/tierContext";
import { useUser } from "../utils/userContext";
import SearchableSelect from "./SearchableSelect";
import styles from "./Dashboard.module.css";

const DASHBOARD_CALENDAR_VIEWPORT_PADDING = 12;
const DASHBOARD_CALENDAR_DEFAULT_WIDTH = 260;

type DatePreset = DashboardDateRange & {
  Key: string;
  Source: string;
};

type KpiCard = {
  key: string;
  label: string;
  value: string;
  meta: string;
  trend?: number | null;
  icon: string;
};

type DashboardDateRange = {
  StartDate: string;
  EndDate: string;
  Label: string;
};

type DashboardClient = {
  ClientId: number;
  ClientName: string;
  HasConfiguredBillingPeriod: boolean;
  IsActive: boolean;
};

type DashboardTopClient = {
  ClientId: number;
  ClientName: string;
  ActiveProspectCount: number;
};

type DashboardTimeStatus = "submitted" | "pending" | "approved" | "rejected" | "invoiced";

type DashboardTimeEntry = {
  TimeId: number;
  WorkDate: string;
  ClientId: number;
  ClientName: string;
  ProspectId: number;
  ProspectName: string;
  JobTypeId: number;
  JobName: string;
  Hours: number;
  BilledDays: number;
  BillableValue: number;
  ApprovalStatus: DashboardTimeStatus;
  DisplayStatus: string;
  InvoiceId: number;
  UserName: string;
};

type DashboardTopProspect = {
  ProspectId: number;
  ProspectName: string;
  ClientName: string;
  BillableDays: number;
  BillableValue: number;
  PendingDays: number | null;
  Percent: number;
};

type DashboardJobType = {
  JobName: string;
  Days: number;
  Percent: number;
  Color: string;
};

const JOB_COLORS = ["#397ffb", "#10b981", "#8b5cf6", "#f59e0b", "#14b8a6", "#ef4444"];

const defaultDashboardRanges = (): Record<string, DashboardDateRange> => {
  const today = new Date();
  const daysSinceMonday = (today.getDay() + 6) % 7;
  const startOfWeek = addDays(today, -daysSinceMonday);
  const endOfWeek = addDays(startOfWeek, 6);
  const lastWeekStart = addDays(startOfWeek, -7);
  const lastWeekEnd = addDays(startOfWeek, -1);
  const last14Start = addDays(today, -13);
  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    this_week: { StartDate: toYmd(startOfWeek), EndDate: toYmd(endOfWeek), Label: "This Week" },
    last_week: { StartDate: toYmd(lastWeekStart), EndDate: toYmd(lastWeekEnd), Label: "Last Week" },
    last_14_days: { StartDate: toYmd(last14Start), EndDate: toYmd(today), Label: "Last 14 Days" },
    this_month: { StartDate: toYmd(thisMonthStart), EndDate: toYmd(thisMonthEnd), Label: "This Month" },
    last_month: { StartDate: toYmd(lastMonthStart), EndDate: toYmd(lastMonthEnd), Label: "Last Month" },
  };
};

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(`${value}T12:00:00`));

const formatHours = (value: number) =>
  value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCurrency = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const toDate = (value: string) => new Date(`${value}T12:00:00`);

const parseYmdAsLocal = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toYmd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatPeriodLabel = (range: DashboardDateRange) =>
  `${parseYmdAsLocal(range.StartDate).toLocaleDateString()} - ${parseYmdAsLocal(range.EndDate).toLocaleDateString()}`;

const previousEquivalentRange = (range: DashboardDateRange): DashboardDateRange => {
  const start = toDate(range.StartDate);
  const end = toDate(range.EndDate);
  const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  const previousEnd = addDays(start, -1);
  const previousStart = addDays(previousEnd, -(days - 1));
  return {
    StartDate: toYmd(previousStart),
    EndDate: toYmd(previousEnd),
    Label: `Previous ${days} days`,
  };
};

const trendText = (trend?: number | null) => {
  if (trend == null) return null;
  const arrow = trend > 0 ? "↗" : trend < 0 ? "↘" : "→";
  return `${arrow} ${Math.abs(trend).toFixed(1)}%`;
};

const getTimeEntryStatusDisplay = (status: DashboardTimeStatus) => {
  if (status === "submitted" || status === "pending") {
    return { label: "Pending", tone: "pending" };
  }

  if (status === "rejected") {
    return { label: "Rejected", tone: "rejected" };
  }

  return { label: status.charAt(0).toUpperCase() + status.slice(1), tone: status };
};

const getTimeEntryStatusTone = (entry: DashboardTimeEntry) => {
  const normalizedDisplay = entry.DisplayStatus.trim().toLowerCase();
  if (normalizedDisplay === "pending" || normalizedDisplay === "submitted") return "pending";
  if (normalizedDisplay === "approved") return "approved";
  if (normalizedDisplay === "rejected" || normalizedDisplay === "denied") return "rejected";
  if (normalizedDisplay === "invoiced") return "invoiced";
  return getTimeEntryStatusDisplay(entry.ApprovalStatus).tone;
};

const normalizeStatus = (status: string | null | undefined, invoiceId?: number): DashboardTimeStatus => {
  if ((invoiceId ?? 0) > 0) return "invoiced";
  if (status === "approved" || status === "rejected" || status === "invoiced" || status === "submitted" || status === "pending") {
    return status;
  }
  return "pending";
};

const getEntryBilledDays = (entry: DashboardTimeEntry) => entry.BilledDays;

const fallbackPresets = (client: DashboardClient | undefined): DatePreset[] => {
  const ranges = defaultDashboardRanges();
  const defaultPresets: DatePreset[] = [
    { Key: "this_week", Source: "default", ...ranges.this_week },
    { Key: "last_week", Source: "default", ...ranges.last_week },
    { Key: "last_14_days", Source: "default", ...ranges.last_14_days },
    { Key: "this_month", Source: "default", ...ranges.this_month },
    { Key: "last_month", Source: "default", ...ranges.last_month },
  ];

  return client?.HasConfiguredBillingPeriod ? defaultPresets.slice(2) : defaultPresets;
};

const resolveDefaultPresetRange = (preset: DatePreset): DatePreset => {
  if (preset.Source !== "default") return preset;

  const currentRange = defaultDashboardRanges()[preset.Key];
  return currentRange
    ? {
        ...preset,
        StartDate: currentRange.StartDate,
        EndDate: currentRange.EndDate,
        Label: currentRange.Label,
      }
    : preset;
};

const mapPreset = (preset: DashboardDatePreset): DatePreset | null => {
  if (!preset.startDate || !preset.endDate) return null;
  return {
    Key: preset.key ?? `${preset.startDate}_${preset.endDate}`,
    StartDate: preset.startDate,
    EndDate: preset.endDate,
    Label: preset.label ?? "Custom Range",
    Source: preset.source ?? "default",
  };
};

const mapClient = (client: DashboardClientOption): DashboardClient => ({
  ClientId: client.clientId ?? 0,
  ClientName: client.clientName ?? "Unnamed Client",
  HasConfiguredBillingPeriod: Boolean(client.hasConfiguredBillingPeriod),
  IsActive: client.isActive ?? true,
});

const mapTopClient = (client: DashboardTopClientChip): DashboardTopClient => ({
  ClientId: client.clientId ?? 0,
  ClientName: client.clientName ?? "Unnamed Client",
  ActiveProspectCount: client.activeProspectCount ?? 0,
});

const mapTimeSeriesPoint = (point: DashboardTimeSeriesPoint) => ({
  Date: point.date ?? "",
  Days: point.billedDays ?? 0,
});

const mapJobType = (item: DashboardBilledDaysByJobType, index: number): DashboardJobType => ({
  JobName: item.jobName ?? "Unassigned",
  Days: item.billedDays ?? 0,
  Percent: item.percent ?? 0,
  Color: JOB_COLORS[index % JOB_COLORS.length],
});

const mapRecentEntry = (entry: DashboardRecentTimeEntry): DashboardTimeEntry => {
  const status = normalizeStatus(entry.approvalStatus, entry.invoiceId);
  return {
    TimeId: entry.timeId ?? 0,
    WorkDate: entry.workDate ?? "",
    ClientId: entry.clientId ?? 0,
    ClientName: entry.clientName ?? "Unknown Client",
    ProspectId: entry.prospectId ?? 0,
    ProspectName: entry.prospectName ?? "Unknown Prospect",
    JobTypeId: entry.jobTypeId ?? 0,
    JobName: entry.jobName ?? "Unassigned",
    Hours: entry.hours ?? 0,
    BilledDays: entry.billedDays ?? entry.hours ?? 0,
    BillableValue: entry.billableValue ?? 0,
    ApprovalStatus: status,
    DisplayStatus: entry.displayStatus ?? getTimeEntryStatusDisplay(status).label,
    InvoiceId: entry.invoiceId ?? 0,
    UserName: entry.userName ?? "Unknown Landman",
  };
};

const mapTopProspect = (item: DashboardTopActiveProspectByBilledDays): DashboardTopProspect => ({
  ProspectId: item.prospectId ?? 0,
  ProspectName: item.prospectName ?? "Unknown Prospect",
  ClientName: item.clientName ?? "Unknown Client",
  BillableDays: item.billableDays ?? 0,
  BillableValue: item.billableValue ?? 0,
  PendingDays: item.pendingApprovalDays ?? null,
  Percent: item.percentOfTotalBilledDays ?? 0,
});

const makeKpiCard = (
  key: string,
  label: string,
  metric: DashboardKpiMetric | undefined,
  meta: string,
  icon: string,
  formatter: (value: number) => string = formatHours,
): KpiCard | null => {
  if (!metric || metric.available === false) return null;
  return {
    key,
    label,
    value: formatter(metric.value ?? 0),
    meta,
    trend: metric.percentChange,
    icon,
  };
};

export default function Dashboard() {
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();
  const { hasFeature } = useTier();
  const fallbackApprovalWorkflowEnabled = hasFeature("approval_workflow");

  const [clientId, setClientId] = useState("all");
  const [selectedRange, setSelectedRange] = useState<DashboardDateRange>(() => defaultDashboardRanges().last_14_days);
  const [selectedPresetKey, setSelectedPresetKey] = useState("last_14_days");
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingPresetSyncClientId, setPendingPresetSyncClientId] = useState<string | null>(null);

  const api = useMemo<ApiClient | null>(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const comparisonRange = useMemo(() => previousEquivalentRange(selectedRange), [selectedRange]);

  useEffect(() => {
    if (!api) return;

    const controller = new AbortController();

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get("Dashboard/summary", {
          signal: controller.signal,
          query: {
            StartDate: selectedRange.StartDate,
            EndDate: selectedRange.EndDate,
            ComparisonStartDate: comparisonRange.StartDate,
            ComparisonEndDate: comparisonRange.EndDate,
            ClientId: clientId === "all" ? undefined : clientId,
            RecentLimit: "5",
            TopProspectsLimit: "5",
          },
        }) as DashboardSummaryResponse;
        setSummary(response);
      } catch (err) {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : "Dashboard data could not be loaded.";
        setError(message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadDashboard();
    return () => controller.abort();
  }, [api, clientId, comparisonRange.EndDate, comparisonRange.StartDate, selectedRange.EndDate, selectedRange.StartDate]);

  const availableClients = useMemo(
    () => (summary?.availableClients ?? []).map(mapClient).filter((client) => client.ClientId > 0),
    [summary?.availableClients],
  );

  const selectedClient = useMemo(
    () => availableClients.find((client) => String(client.ClientId) === clientId),
    [availableClients, clientId],
  );

  const presets = useMemo(() => {
    const apiPresets = (summary?.availableDatePresets ?? [])
      .map(mapPreset)
      .filter((preset): preset is DatePreset => Boolean(preset))
      .map(resolveDefaultPresetRange);
    return apiPresets.length ? apiPresets : fallbackPresets(selectedClient);
  }, [selectedClient, summary?.availableDatePresets]);

  const resolvePresetSelection = (preset: DatePreset): DatePreset => resolveDefaultPresetRange(preset);

  useEffect(() => {
    const summaryScope = summary?.selectedClient?.clientId ? String(summary.selectedClient.clientId) : "all";
    if (!summary || summaryScope !== clientId || pendingPresetSyncClientId !== clientId || !presets.length) return;
    setPendingPresetSyncClientId(null);

    const nextPreset = selectedPresetKey === "custom"
      ? null
      : presets.find((preset) => preset.Key === selectedPresetKey);
    const resolvedPreset = nextPreset ? resolvePresetSelection(nextPreset) : null;
    if (
      resolvedPreset &&
      (resolvedPreset.StartDate !== selectedRange.StartDate || resolvedPreset.EndDate !== selectedRange.EndDate)
    ) {
      setSelectedRange(resolvedPreset);
    }
  }, [clientId, pendingPresetSyncClientId, presets, selectedPresetKey, selectedRange.EndDate, selectedRange.StartDate, summary]);

  const topClientChips = useMemo(
    () => (summary?.topClientChips ?? []).map(mapTopClient).filter((client) => client.ClientId > 0),
    [summary?.topClientChips],
  );

  const kpis = useMemo<KpiCard[]>(() => {
    const meta = selectedRange.Label;
    const context = clientId === "all" ? "All Clients" : selectedClient?.ClientName ?? "Selected Client";
    const backendApprovalWorkflowEnabled = summary?.featureAvailability?.approvalWorkflow;
    const approvalWorkflowEnabled = backendApprovalWorkflowEnabled ?? fallbackApprovalWorkflowEnabled;
    const next = [
      makeKpiCard("billable-days", "Billable Days", summary?.kpis?.billableDays, meta, "◷"),
      makeKpiCard("billable-value", "Est Billable Value", summary?.kpis?.billableValue, meta, "✓", formatCurrency),
    ].filter((card): card is KpiCard => Boolean(card));

    const pendingApproval = makeKpiCard(
      "pending-approval-days",
      "Pending Approval Days",
      summary?.kpis?.pendingApprovalDays,
      meta,
      "!",
    );
    if (approvalWorkflowEnabled && pendingApproval) next.push(pendingApproval);

    const activeProspects = makeKpiCard("active-prospects", "Active Prospects", summary?.kpis?.activeProspects, context, "⌁", (value) => String(Math.round(value)));
    if (activeProspects) next.push(activeProspects);

    const activeClients = makeKpiCard("active-clients", "Active Clients", summary?.kpis?.activeClients, "Current", "□", (value) => String(Math.round(value)));
    if (clientId === "all" && activeClients) next.push(activeClients);

    return next;
  }, [clientId, fallbackApprovalWorkflowEnabled, selectedClient?.ClientName, selectedRange.Label, summary?.featureAvailability?.approvalWorkflow, summary?.kpis]);

  const timeSeries = useMemo(
    () => (summary?.timeSeries ?? []).map(mapTimeSeriesPoint).filter((point) => point.Date),
    [summary?.timeSeries],
  );

  const billedDaysByJobType = useMemo(
    () => (summary?.billedDaysByJobType ?? []).map(mapJobType),
    [summary?.billedDaysByJobType],
  );

  const recentEntries = useMemo(
    () => (summary?.recentTimeEntries ?? []).map(mapRecentEntry).filter((entry) => entry.TimeId > 0),
    [summary?.recentTimeEntries],
  );

  const topProspects = useMemo(
    () => (summary?.topActiveProspectsByBilledDays ?? []).map(mapTopProspect).filter((prospect) => prospect.ProspectId > 0),
    [summary?.topActiveProspectsByBilledDays],
  );

  const approvalWorkflowEnabled = summary?.featureAvailability?.approvalWorkflow ?? fallbackApprovalWorkflowEnabled;
  const isRefreshing = loading && Boolean(summary);
  const showDefaultRangeHelper = clientId !== "all" && selectedClient?.HasConfiguredBillingPeriod === false;
  const clientOptions = useMemo(
    () => [
      { value: 0, label: "All Clients" },
      ...availableClients.map((client) => ({ value: client.ClientId, label: client.ClientName })),
    ],
    [availableClients],
  );

  return (
    <div className={`main-articles ${styles["dashboard-page"]}`}>
      <section className={styles["dashboard-filter-card"]} aria-label="Dashboard filters">
        <div className={styles["dashboard-filter-grid"]}>
          <div>
            <div className={`${styles["dashboard-preset-row"]} ${styles["dashboard-client-chip-row"]}`} aria-label="Top clients by active prospects">
              <button
                type="button"
                className={`${styles["dashboard-preset-chip"]} ${clientId === "all" ? styles["dashboard-preset-chip--active"] : ""}`}
                aria-pressed={clientId === "all"}
                onClick={() => {
                  setClientId("all");
                  setPendingPresetSyncClientId("all");
                }}
              >
                All Clients
              </button>
              {topClientChips.map((client) => (
                <button
                  key={client.ClientId}
                  type="button"
                  className={`${styles["dashboard-preset-chip"]} ${
                    clientId === String(client.ClientId) ? styles["dashboard-preset-chip--active"] : ""
                  }`}
                  aria-pressed={clientId === String(client.ClientId)}
                  onClick={() => {
                    const nextClientId = String(client.ClientId);
                    setClientId(nextClientId);
                    setPendingPresetSyncClientId(nextClientId);
                  }}
                  title={`${client.ActiveProspectCount} active prospect${client.ActiveProspectCount === 1 ? "" : "s"}`}
                >
                  {client.ClientName}
                </button>
              ))}
            </div>
            <label className={styles["dashboard-field-label"]} htmlFor="dashboardClient">
              Client
            </label>
            <SearchableSelect
              id="dashboardClient"
              options={clientOptions}
              value={clientId === "all" ? 0 : Number(clientId)}
              onChange={(value) => {
                const nextClientId = value ? String(value) : "all";
                setClientId(nextClientId);
                setPendingPresetSyncClientId(nextClientId);
              }}
              className={styles["dashboard-control"]}
              placeholder="Search clients..."
            />
          </div>
          <div>
            <div className={styles["dashboard-preset-row"]}>
              {presets.map((preset) => (
                <button
                  key={preset.Key}
                  type="button"
                  className={`${styles["dashboard-preset-chip"]} ${
                    selectedPresetKey === preset.Key
                      ? styles["dashboard-preset-chip--active"]
                      : ""
                  }`}
                  aria-pressed={selectedPresetKey === preset.Key}
                  onClick={() => {
                    setSelectedPresetKey(preset.Key);
                    setSelectedRange(resolvePresetSelection(preset));
                  }}
                >
                  {preset.Label}
                </button>
              ))}
            </div>
            <DashboardDateRangeField
              range={selectedRange}
              onChange={(nextRange) => {
                setSelectedPresetKey("custom");
                setSelectedRange({ ...nextRange, Label: "Custom Range" });
              }}
            />
          </div>
        </div>
        {showDefaultRangeHelper && (
          <p className={styles["dashboard-helper"]}>
            Using default date ranges because this client has no billing period configured.
          </p>
        )}
        {error && (
          <p className={styles["dashboard-error"]} role="alert">
            {error}
          </p>
        )}
        {isRefreshing && (
          <div className={styles["dashboard-refresh-indicator"]} role="status" aria-live="polite">
            <span className={styles["dashboard-refresh-bar"]} aria-hidden="true" />
            <span>Updating dashboard...</span>
          </div>
        )}
      </section>

      <section className={styles["dashboard-kpi-grid"]} aria-label="Dashboard KPI summary" aria-busy={isRefreshing}>
        {loading && !summary ? (
          <DashboardKpiSkeletonCards />
        ) : kpis.map((card) => {
          const trend = trendText(card.trend);
          return (
            <article
              key={card.key}
              className={styles["dashboard-kpi-card"]}
              data-testid={`dashboard-kpi-${card.key}`}
            >
              <div className={styles["dashboard-kpi-icon"]} aria-hidden="true">{card.icon}</div>
              <div>
                <div className={styles["dashboard-kpi-label"]}>{card.label}</div>
                <div className={styles["dashboard-kpi-value"]}>{card.value}</div>
                <div className={styles["dashboard-kpi-meta"]}>
                  <span>{card.meta}</span>
                  {trend && (
                    <span className={card.trend && card.trend < 0 ? styles["dashboard-trend-down"] : styles["dashboard-trend-up"]}>
                      {trend}
                    </span>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <div className={styles["dashboard-grid-two"]}>
        <DashboardCard title="Billed Days Over Time" isRefreshing={isRefreshing}>
          <LineChart data={timeSeries} />
        </DashboardCard>
        <DashboardCard title="Billed Time by Job Type" isRefreshing={isRefreshing}>
          <DonutChart data={billedDaysByJobType} />
        </DashboardCard>
      </div>

      <div className={styles["dashboard-grid-two"]}>
        <DashboardCard title="Recent Time Entries" isRefreshing={isRefreshing}>
          <RecentEntriesTable entries={recentEntries} />
        </DashboardCard>
        <DashboardCard title="Top Active Prospects by Billed Days" isRefreshing={isRefreshing}>
          <TopProspectsList prospects={topProspects} showClient={clientId === "all"} approvalWorkflowEnabled={approvalWorkflowEnabled} />
        </DashboardCard>
      </div>
    </div>
  );
}

function DashboardKpiSkeletonCards() {
  return (
    <>
      <span className={styles["dashboard-sr-only"]} role="status">
        Loading dashboard metrics.
      </span>
      {Array.from({ length: 4 }).map((_, index) => (
        <article key={index} className={`${styles["dashboard-kpi-card"]} ${styles["dashboard-kpi-card--loading"]}`} aria-hidden="true">
          <span className={`${styles["dashboard-skeleton"]} ${styles["dashboard-skeleton-icon"]}`} />
          <span>
            <span className={`${styles["dashboard-skeleton"]} ${styles["dashboard-skeleton-label"]}`} />
            <span className={`${styles["dashboard-skeleton"]} ${styles["dashboard-skeleton-value"]}`} />
            <span className={`${styles["dashboard-skeleton"]} ${styles["dashboard-skeleton-meta"]}`} />
          </span>
        </article>
      ))}
    </>
  );
}

function DashboardDateRangeField({
  range,
  onChange,
}: {
  range: DashboardDateRange;
  onChange: (range: DashboardDateRange) => void;
}) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<{ from: Date | undefined; to?: Date }>({
    from: parseYmdAsLocal(range.StartDate),
    to: parseYmdAsLocal(range.EndDate),
  });
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(parseYmdAsLocal(range.StartDate));
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0, ready: false });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraftRange({
        from: parseYmdAsLocal(range.StartDate),
        to: parseYmdAsLocal(range.EndDate),
      });
      setVisibleMonth(parseYmdAsLocal(range.StartDate));
      setIsSelectingEnd(false);
    }
  }, [isOpen, range.EndDate, range.StartDate]);

  const updatePopoverPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const visualWidth = popoverRef.current?.getBoundingClientRect().width || DASHBOARD_CALENDAR_DEFAULT_WIDTH;
    const maxLeft = Math.max(
      DASHBOARD_CALENDAR_VIEWPORT_PADDING,
      window.innerWidth - visualWidth - DASHBOARD_CALENDAR_VIEWPORT_PADDING,
    );
    const desiredLeft = rect.left - 50;
    setPopoverPos({
      top: rect.bottom + 4,
      left: Math.min(Math.max(DASHBOARD_CALENDAR_VIEWPORT_PADDING, desiredLeft), maxLeft),
      ready: true,
    });
  };

  useLayoutEffect(() => {
    if (!isOpen || isMobile) return;
    updatePopoverPosition();
    const handleReposition = () => updatePopoverPosition();
    window.addEventListener("resize", handleReposition);
    document.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      document.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [isOpen]);

  const openPicker = () => {
    setDraftRange({
      from: parseYmdAsLocal(range.StartDate),
      to: parseYmdAsLocal(range.EndDate),
    });
    setIsSelectingEnd(false);
    setVisibleMonth(parseYmdAsLocal(range.StartDate));

    if (!isMobile && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 4,
        left: Math.max(DASHBOARD_CALENDAR_VIEWPORT_PADDING, rect.left - 50),
        ready: false,
      });
    } else {
      setPopoverPos((prev) => ({ ...prev, ready: true }));
    }

    setIsOpen(true);
  };

  const closePicker = () => {
    setIsOpen(false);
    setIsSelectingEnd(false);
  };

  const handleDayClick = (day: Date, modifiers: { disabled?: boolean }) => {
    if (modifiers.disabled) return;

    if (!isSelectingEnd) {
      setDraftRange({ from: day, to: undefined });
      setIsSelectingEnd(true);
      setVisibleMonth(day);
      return;
    }

    const first = draftRange.from;
    if (!first) {
      setDraftRange({ from: day, to: undefined });
      setVisibleMonth(day);
      return;
    }

    const from = first <= day ? first : day;
    const to = first <= day ? day : first;
    setDraftRange({ from, to });
    onChange({ StartDate: toYmd(from), EndDate: toYmd(to), Label: "Custom Range" });
    closePicker();
  };

  const calendar = (
    <div
      ref={popoverRef}
      className={`${styles["dashboard-rdp-wrap"]} ${
        isMobile ? styles["dashboard-mobile-calendar-card"] : styles["dashboard-calendar-card"]
      }`}
      role="dialog"
      aria-label="Select dashboard date range"
      data-testid="dashboard-date-range-dialog"
      style={
        isMobile
          ? undefined
          : {
              top: popoverPos.top,
              left: popoverPos.left,
              visibility: popoverPos.ready ? "visible" : "hidden",
            }
      }
    >
      {isMobile && (
        <div className={styles["dashboard-mobile-calendar-header"]}>
          <div className={styles["dashboard-mobile-calendar-title"]}>Select Date Range</div>
          <button type="button" className={styles["dashboard-mobile-calendar-close"]} onClick={closePicker}>
            Close
          </button>
        </div>
      )}
      <div className={styles["dashboard-calendar-hint"]}>
        {isSelectingEnd ? "Select the end date." : "Select a start date."}
      </div>
      <DayPicker
        mode="range"
        numberOfMonths={1}
        selected={draftRange}
        month={visibleMonth}
        onMonthChange={setVisibleMonth}
        onDayClick={handleDayClick}
        showOutsideDays
      />
    </div>
  );

  return (
    <div>
      <label className={styles["dashboard-field-label"]} htmlFor="dashboardDateRange">
        Date range
      </label>
      <button
        id="dashboardDateRange"
        ref={triggerRef}
        type="button"
        className={styles["dashboard-range-trigger"]}
        onClick={openPicker}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className={styles["dashboard-range-trigger-value"]}>
          {range.Label}: {formatPeriodLabel(range)}
        </span>
        <span className={styles["dashboard-range-trigger-icon"]} aria-hidden="true">
          <CalendarMiniIcon />
        </span>
      </button>
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          isMobile ? <div className={styles["dashboard-mobile-calendar-backdrop"]}>{calendar}</div> : calendar,
          document.body,
        )}
    </div>
  );
}

function CalendarMiniIcon() {
  return (
    <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <rect width="28" height="28" rx="6" fill="#397FFB" />
      <path d="M11.3335 7.3335V9.3335" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.6665 7.3335V9.3335" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.3335 12.0603H19.6668" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 11.6668V17.3335C20 19.3335 19 20.6668 16.6667 20.6668H11.3333C9 20.6668 8 19.3335 8 17.3335V11.6668C8 9.66683 9 8.3335 11.3333 8.3335H16.6667C19 8.3335 20 9.66683 20 11.6668Z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16.463 15.1331H16.469" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M16.463 17.1331H16.469" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13.9972 15.1331H14.0031" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13.9972 17.1331H14.0031" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M11.5294 15.1331H11.5354" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M11.5294 17.1331H11.5354" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function DashboardCard({
  title,
  action,
  children,
  isRefreshing = false,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  isRefreshing?: boolean;
}) {
  return (
    <section className={styles["dashboard-card"]} aria-busy={isRefreshing}>
      <div className={styles["dashboard-card-header"]}>
        <h2 className={styles["dashboard-card-title"]}>{title}</h2>
        {action}
      </div>
      <div className={styles["dashboard-card-body"]}>{children}</div>
    </section>
  );
}

function LineChart({ data }: { data: Array<{ Date: string; Days: number }> }) {
  const width = 640;
  const height = 260;
  const padding = { top: 20, right: 24, bottom: 34, left: 42 };
  const maxDays = Math.max(...data.map((item) => item.Days), 1);
  const xStep = data.length > 1 ? (width - padding.left - padding.right) / (data.length - 1) : 0;
  const y = (days: number) => padding.top + (1 - days / maxDays) * (height - padding.top - padding.bottom);
  const points = data.map((item, index) => ({
    x: padding.left + index * xStep,
    y: y(item.Days),
    ...item,
  }));
  const linePath = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1]?.x ?? padding.left} ${height - padding.bottom} L ${padding.left} ${height - padding.bottom} Z`;
  const total = data.reduce((sum, item) => sum + item.Days, 0);
  const labelStep = data.length <= 7 ? 1 : Math.ceil((data.length - 1) / 5);
  const shouldShowDateLabel = (index: number) =>
    index === 0 || index === points.length - 1 || index % labelStep === 0;

  if (!data.some((item) => item.Days > 0)) {
    return <div className={styles["dashboard-empty"]}>No billed days in this range.</div>;
  }

  return (
    <>
      <p className={styles["dashboard-chart-summary"]}>
        {formatHours(total)} billed days across {data.length} calendar days.
      </p>
      <svg className={styles["dashboard-line-chart"]} viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="dashboardLineTitle dashboardLineDesc">
        <title id="dashboardLineTitle">Billed days over time</title>
        <desc id="dashboardLineDesc">Line chart showing billed days by date for the selected range.</desc>
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const gridY = padding.top + ratio * (height - padding.top - padding.bottom);
          return <line key={ratio} className={styles["dashboard-line-grid"]} x1={padding.left} y1={gridY} x2={width - padding.right} y2={gridY} />;
        })}
        <line className={styles["dashboard-line-axis"]} x1={padding.left} y1={height - padding.bottom} x2={width - padding.right} y2={height - padding.bottom} />
        <path className={styles["dashboard-line-area"]} d={areaPath} />
        <path className={styles["dashboard-line-path"]} d={linePath} />
        {points.map((point, index) => (
          <g
            key={point.Date}
            className={styles["dashboard-line-point-group"]}
            tabIndex={0}
            role="listitem"
            aria-label={`${formatDate(point.Date)}: ${formatHours(point.Days)} billed days`}
            data-testid={`dashboard-line-point-${point.Date}`}
          >
            <line
              className={styles["dashboard-line-hover-guide"]}
              x1={point.x}
              y1={padding.top}
              x2={point.x}
              y2={height - padding.bottom}
            />
            <rect
              className={styles["dashboard-line-callout-bg"]}
              x={Math.min(Math.max(point.x - 48, padding.left), width - padding.right - 96)}
              y={Math.max(point.y - 38, 6)}
              width="96"
              height="28"
              rx="8"
            />
            <text
              className={styles["dashboard-line-callout-text"]}
              x={Math.min(Math.max(point.x, padding.left + 48), width - padding.right - 48)}
              y={Math.max(point.y - 20, 24)}
              textAnchor="middle"
            >
              {`${formatHours(point.Days)}d · ${formatDate(point.Date)}`}
            </text>
            <circle className={styles["dashboard-line-hit-area"]} cx={point.x} cy={point.y} r="14" />
            <circle className={styles["dashboard-line-point"]} cx={point.x} cy={point.y} r="4" />
            {shouldShowDateLabel(index) && (
              <text className={styles["dashboard-chart-label"]} x={point.x} y={height - 10} textAnchor="middle">
                {formatDate(point.Date)}
              </text>
            )}
          </g>
        ))}
      </svg>
    </>
  );
}

function DonutChart({ data }: { data: Array<{ JobName: string; Days: number; Percent: number; Color: string }> }) {
  const total = data.reduce((sum, item) => sum + item.Days, 0);
  const [activeJobName, setActiveJobName] = useState<string | null>(null);
  let offset = 25;

  if (!data.length || total === 0) {
    return <div className={styles["dashboard-empty"]}>No billed days by job type in this range.</div>;
  }

  return (
    <div className={styles["dashboard-donut-layout"]}>
      <div className={styles["dashboard-donut"]}>
        <svg viewBox="0 0 42 42" role="img" aria-labelledby="dashboardDonutTitle dashboardDonutDesc">
          <title id="dashboardDonutTitle">Billed time by job type</title>
          <desc id="dashboardDonutDesc">Donut chart showing selected range billed days grouped by job type.</desc>
          <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#eef4ff" strokeWidth="8" />
          {data.map((item) => {
            const dash = `${item.Percent} ${100 - item.Percent}`;
            const isActive = activeJobName === item.JobName;
            const segment = (
              <circle
                key={item.JobName}
                className={`${styles["dashboard-donut-segment"]} ${isActive ? styles["dashboard-donut-segment--active"] : ""}`}
                cx="21"
                cy="21"
                r="15.915"
                fill="transparent"
                stroke={item.Color}
                strokeWidth="8"
                strokeDasharray={dash}
                strokeDashoffset={offset}
                tabIndex={0}
                role="listitem"
                aria-label={`${item.JobName}: ${formatHours(item.Days)} billed days, ${item.Percent.toFixed(1)} percent`}
                data-testid={`dashboard-donut-segment-${item.JobName}`}
                onMouseEnter={() => setActiveJobName(item.JobName)}
                onMouseLeave={() => setActiveJobName(null)}
                onFocus={() => setActiveJobName(item.JobName)}
                onBlur={() => setActiveJobName(null)}
              >
                <title>{`${item.JobName}: ${formatHours(item.Days)} billed days (${item.Percent.toFixed(1)}%)`}</title>
              </circle>
            );
            offset -= item.Percent;
            return segment;
          })}
        </svg>
      </div>
      <div className={styles["dashboard-legend"]}>
        <div className={styles["dashboard-donut-center"]}>
          <div className={styles["dashboard-donut-value"]}>{formatHours(total)}</div>
          <div className={styles["dashboard-donut-label"]}>Billed Days</div>
        </div>
        {data.map((item) => (
          <div
            key={item.JobName}
            className={`${styles["dashboard-legend-row"]} ${activeJobName === item.JobName ? styles["dashboard-legend-row--active"] : ""}`}
            tabIndex={0}
            role="button"
            aria-label={`Highlight ${item.JobName}: ${formatHours(item.Days)} billed days, ${item.Percent.toFixed(1)} percent`}
            onMouseEnter={() => setActiveJobName(item.JobName)}
            onMouseLeave={() => setActiveJobName(null)}
            onFocus={() => setActiveJobName(item.JobName)}
            onBlur={() => setActiveJobName(null)}
          >
            <span className={styles["dashboard-color-dot"]} style={{ backgroundColor: item.Color }} />
            <span>{item.JobName}</span>
            <strong>{formatHours(item.Days)}d ({item.Percent.toFixed(1)}%)</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentEntriesTable({
  entries,
}: {
  entries: DashboardTimeEntry[];
}) {
  if (!entries.length) {
    return <div className={styles["dashboard-empty"]}>No recent time entries in this range.</div>;
  }

  return (
    <div className={styles["dashboard-table-wrap"]}>
      <table className={styles["dashboard-table"]}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Landman</th>
            <th>Prospect</th>
            <th>Job</th>
            <th className={styles["dashboard-number"]}>Days</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const statusTone = getTimeEntryStatusTone(entry);

            return (
              <tr key={entry.TimeId} data-testid={`dashboard-time-entry-${entry.TimeId}`}>
                <td>{formatDate(entry.WorkDate)}</td>
                <td>{entry.UserName}</td>
                <td>
                  <strong>{entry.ProspectName}</strong>
                  <div className={styles["dashboard-prospect-meta"]}>{entry.ClientName}</div>
                </td>
                <td>{entry.JobName}</td>
                <td className={styles["dashboard-number"]}>{formatHours(getEntryBilledDays(entry))}</td>
                <td>
                  <span className={`${styles["dashboard-status"]} ${styles[`dashboard-status-${statusTone}`]}`}>
                    {entry.DisplayStatus}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TopProspectsList({
  prospects,
  showClient,
  approvalWorkflowEnabled,
}: {
  prospects: DashboardTopProspect[];
  showClient: boolean;
  approvalWorkflowEnabled: boolean;
}) {
  if (!prospects.length) {
    return <div className={styles["dashboard-empty"]}>No active prospect billed days in this range.</div>;
  }

  return (
    <div>
      <p className={styles["dashboard-progress-helper"]}>Bars show each prospect's share of selected-range billed days.</p>
      {prospects.map((prospect) => (
        <div key={prospect.ProspectId} className={styles["dashboard-prospect-row"]} data-testid={`dashboard-top-prospect-${prospect.ProspectId}`}>
          <div>
            <div className={styles["dashboard-prospect-name"]}>{prospect.ProspectName}</div>
            <div className={styles["dashboard-prospect-meta"]}>
              {showClient && `${prospect.ClientName} · `}
              {formatCurrency(prospect.BillableValue)}
              {approvalWorkflowEnabled && prospect.PendingDays != null && ` · ${formatHours(prospect.PendingDays)} pending approval days`}
            </div>
          </div>
          <strong>{formatHours(prospect.BillableDays)} days</strong>
          <div className={styles["dashboard-progress"]} aria-label={`${prospect.Percent.toFixed(1)} percent of selected range billed days`}>
            <div className={styles["dashboard-progress-bar"]} style={{ width: `${Math.min(100, prospect.Percent)}%` }} />
            <span className={styles["dashboard-progress-label"]}>{prospect.Percent.toFixed(1)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}
